<script lang="ts">
  import { round } from "@/Util/utils";
  import { cn } from "@/lib/utils";

  // Global averages
  export let globalMaleAvgFtp: number; // watts
  export let globalMaleAvgPp: number; // watts
  export let globalMaleAvgHie: number; // kJ

  export let globalFemaleAvgFtp: number; // watts
  export let globalFemaleAvgPp: number; // watts
  export let globalFemaleAvgHie: number; // kJ

  export let globalMaleAvgFtp2024: number; // watts
  export let globalMaleAvgPp2024: number; // watts
  export let globalMaleAvgHie2024: number; // kJ

  export let globalFemaleAvgFtp2024: number; // watts
  export let globalFemaleAvgPp2024: number; // watts
  export let globalFemaleAvgHie2024: number; // kJ

  // User values
  export let userFtp: number; // watts
  export let userPp: number; // watts
  export let userHie: number; // kJ

  // Animation flag from parent (in-view)
  export let revealed: boolean = false;

  // Colors
  const accent = "#5EE6E8";
  const global2025Color = "#4FD9A8"; // vivid teal-mint, slightly darker and less neon
  const global2024Color = "#6FA2EE"; // bright blue, slightly darker and less neon // #E86FAF bright pink, slightly darker and less neon
  const youColor = "#4FD9A8"; 

  // Bar widths are relative to max of the two values per metric
  function ratios(male: number, female: number, male2024: number, female2024: number, user: number) {
    const max = Math.max(Math.abs(male ?? 0), Math.abs(female ?? 0), Math.abs(male2024 ?? 0), Math.abs(female2024 ?? 0), Math.abs(user ?? 0), 1);
    return {
      malePct: Math.min(100, (Math.abs(male ?? 0) / max) * 100),
      femalePct: Math.min(100, (Math.abs(female ?? 0) / max) * 100),
      male2024Pct: Math.min(100, (Math.abs(male2024 ?? 0) / max) * 100),
      female2024Pct: Math.min(100, (Math.abs(female2024 ?? 0) / max) * 100),
      userPct: Math.min(100, (Math.abs(user ?? 0) / max) * 100),
    };
  }

  function ratioPair(a: number, b: number) {
    const max = Math.max(Math.abs(a ?? 0), Math.abs(b ?? 0), 1);
    return {
      a: Math.min(100, (Math.abs(a ?? 0) / max) * 100),
      b: Math.min(100, (Math.abs(b ?? 0) / max) * 100),
    };
  }

  // Compute pairs
  // $: ftpRatios = ratios(globalMaleAvgFtp ?? 0, globalFemaleAvgFtp ?? 0, globalMaleAvgFtp2024 ?? 0, globalFemaleAvgFtp2024 ?? 0, userFtp ?? 0);
  // $: ppRatios = ratios(globalMaleAvgPp ?? 0, globalFemaleAvgPp ?? 0, globalMaleAvgPp2024 ?? 0, globalFemaleAvgPp2024 ?? 0, userPp ?? 0);
  // $: hieRatios = ratios(globalMaleAvgHie ?? 0, globalFemaleAvgHie ?? 0, globalMaleAvgHie2024 ?? 0, globalFemaleAvgHie2024 ?? 0, userHie ?? 0);
  $: ftpRatioFemale = ratioPair(globalFemaleAvgFtp ?? 0, globalFemaleAvgFtp2024 ?? 0);
  $: ftpRatioMale = ratioPair(globalMaleAvgFtp ?? 0, globalMaleAvgFtp2024 ?? 0);
  $: ppRatioFemale = ratioPair(globalFemaleAvgPp ?? 0, globalFemaleAvgPp2024 ?? 0);
  $: ppRatioMale = ratioPair(globalMaleAvgPp ?? 0, globalMaleAvgPp2024 ?? 0);
  $: hieRatioFemale = ratioPair(globalFemaleAvgHie ?? 0, globalFemaleAvgHie2024 ?? 0);
  $: hieRatioMale = ratioPair(globalMaleAvgHie ?? 0, globalMaleAvgHie2024 ?? 0);

  // Delta helpers
  function deltaPct(user: number, global: number) {
    const g = Math.abs(global ?? 0);
    if (g <= 0) return 0;
    return ((Math.abs(user ?? 0) - g) / g) * 100;
  }
  $: ftpDeltaMale = deltaPct(userFtp ?? 0, globalMaleAvgFtp ?? 0);
  $: ppDeltaMale = deltaPct(userPp ?? 0, globalMaleAvgPp ?? 0);
  $: hieDeltaMale = deltaPct(userHie ?? 0, globalMaleAvgHie ?? 0);
  $: ftpDeltaFemale = deltaPct(userFtp ?? 0, globalFemaleAvgFtp ?? 0);
  $: ppDeltaFemale = deltaPct(userPp ?? 0, globalFemaleAvgPp ?? 0);
  $: hieDeltaFemale = deltaPct(userHie ?? 0, globalFemaleAvgHie ?? 0);

  function deltaText(value: number, positiveLabel: string, negativeLabel: string) {
    const sign = value >= 0 ? "+" : "−"; // use minus symbol
    const pct = Math.abs(round(value, 0));
    const phrase = value >= 0 ? positiveLabel : negativeLabel;
    return `${sign}${pct}%`;
  }

  function percentOrMinWidth(pct, minPx = 13) {
    let scaled = 100 - (100-pct) * 5;
    return scaled < 5 ? `${minPx}px` : scaled + '%';
  }

</script>

<div class="card-shell" style="width:300px;">
  <div class={cn("card-inner")}>
    <!-- Moving glow lines background -->
    <div class={cn("glow-lines")} aria-hidden="true"></div>
    <!-- Woven texture overlay -->
    <div class="woven" aria-hidden="true"></div>

    <!-- Header -->
    <div class="header">
      <div class="title">2025 vs 2024</div>
      <div class="legend">
        <div class="chip">
          <span class="dot" style={`background:${global2025Color}`}></span>
          <span>2025</span>
        </div>
        <div class="chip">
          <span class="dot" style={`background:${global2024Color}`}></span>
          <span>2024</span>
        </div>
        <!-- <div class="chip">
          <span class="dot" style={`background:${youColor}`}></span>
          <span>You</span>
        </div> -->
      </div>
    </div>

    <!-- Metric blocks -->
    <div class="metric-block">
      <div class="label-row">
        <div class="label">Threshold Power</div>
        <div class='flex-1'/>
        <!-- <div class="delta-chip" style={`border-color:${globalFemaleColor}; color:${globalFemaleColor};`}>{deltaText(ftpDeltaFemale, "above", "below")}</div>
        <div class="delta-chip" style={`border-color:${globalMaleColor}; color:${globalMaleColor};`}>{deltaText(ftpDeltaMale, "above", "below")}</div> -->

      </div>
      <div class="grid-bg"></div>
      <div class="bars">
        <div class="flex flex-col gap-0 mb-[1px]">
          <span class='text-xs'>Women</span>
          <div class="bar-row">
            <div class="bar global last-year" style={`--bar-clr:${global2024Color}; --target:${revealed ? percentOrMinWidth(ftpRatioFemale.b) : '0%'};`}>
              <span class="end-label">{round(globalFemaleAvgFtp2024 ?? 0, 0)} W</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalFemaleAvgFtp2024 ?? 0} W in 2024</span> -->
        </div>
        <div class="flex flex-col gap-0 mb-[1px]">
          <div class="bar-row">
            <div class="bar global" style={`--bar-clr:${global2025Color}; --target:${revealed ? percentOrMinWidth(ftpRatioFemale.a) : '0%'};`}>
              <span class="end-label">{round(globalFemaleAvgFtp ?? 0, 0)} W</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalFemaleAvgFtp2024 ?? 0} W in 2024</span> -->
        </div>
        <div class="flex flex-col gap-0 mb-[1px]">
          <span class='text-xs'>Men</span>
          <div class="bar-row">
            <div class="bar global last-year" style={`--bar-clr:${global2024Color}; --target:${revealed ? percentOrMinWidth(ftpRatioMale.b) : '0%'};`}>
              <span class="end-label">{round(globalMaleAvgFtp2024 ?? 0, 0)} W</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalMaleAvgFtp2024 ?? 0} W in 2024</span> -->
        </div>
        <div class="flex flex-col gap-0 mb-[1px]">
          <div class="bar-row">
            <div class="bar global" style={`--bar-clr:${global2025Color}; --target:${revealed ? percentOrMinWidth(ftpRatioMale.a) : '0%'};`}>
              <span class="end-label">{round(globalMaleAvgFtp ?? 0, 0)} W</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalMaleAvgFtp2024 ?? 0} W in 2024</span> -->
        </div>
        <!-- <div class="bar-row">
          <div class="bar you" style={`--bar-clr:${youColor}; --target:${revealed ? percentOrMinWidth(ftpRatios.userPct) : '0%'};`}>
            <span class="end-label">{round(userFtp ?? 0, 0)} W</span>
          </div>
        </div> -->
      </div>
    </div>

    <div class="metric-block">
      <div class="label-row">
        <div class="label">Peak Power</div>
        <div class='flex-1'/>
        <!-- <div class="delta-chip" style={`border-color:${globalFemaleColor}; color:${globalFemaleColor};`}>{deltaText(ppDeltaFemale, "above", "below")}</div>
        <div class="delta-chip" style={`border-color:${globalMaleColor}; color:${globalMaleColor};`}>{deltaText(ppDeltaMale, "above", "below")}</div> -->

      </div>
      <div class="grid-bg"></div>
      <div class="bars">
        <div class="flex flex-col gap-0 mb-[1px]">
          <span class='text-xs'>Women</span>
          <div class="bar-row">
            <div class="bar global last-year" style={`--bar-clr:${global2024Color}; --target:${revealed ? percentOrMinWidth(ppRatioFemale.b) : '0%'};`}>
              <span class="end-label">{round(globalFemaleAvgPp2024 ?? 0, 0)} W</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalFemaleAvgPp2024 ?? 0} W in 2024</span> -->
        </div>
        <div class="flex flex-col gap-0 mb-[1px]">
          <div class="bar-row">
            <div class="bar global" style={`--bar-clr:${global2025Color}; --target:${revealed ? percentOrMinWidth(ppRatioFemale.a) : '0%'};`}>
              <span class="end-label">{round(globalFemaleAvgPp ?? 0, 0)} W</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalFemaleAvgPp2024 ?? 0} W in 2024</span> -->
        </div>
        <div class="flex flex-col gap-0 mb-[1px]">
          <span class='text-xs'>Men</span>
          <div class="bar-row">
            <div class="bar global last-year" style={`--bar-clr:${global2024Color}; --target:${revealed ? percentOrMinWidth(ppRatioMale.b) : '0%'};`}>
              <span class="end-label">{round(globalMaleAvgPp2024 ?? 0, 0)} W</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalMaleAvgPp2024 ?? 0} W in 2024</span> -->
        </div>
        <div class="flex flex-col gap-0 mb-[1px]">
          <div class="bar-row">
            <div class="bar global" style={`--bar-clr:${global2025Color}; --target:${revealed ? percentOrMinWidth(ppRatioMale.a) : '0%'};`}>
              <span class="end-label">{round(globalMaleAvgPp ?? 0, 0)} W</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalMaleAvgPp2024 ?? 0} W in 2024</span> -->
        </div>
        <!-- <div class="bar-row">
          <div class="bar you" style={`--bar-clr:${youColor}; --target:${revealed ? percentOrMinWidth(ppRatios.userPct) : '0%'};`}>
            <span class="end-label">{round(userPp ?? 0, 0)} W</span>
          </div>
        </div> -->
      </div>
    </div>

    <div class="metric-block">
      <div class="label-row">
        <div class="label">High Intensity Energy</div>
        <div class='flex-1'/>
        <!-- <div class="delta-chip" style={`border-color:${globalFemaleColor}; color:${globalFemaleColor};`}>{deltaText(hieDeltaFemale, "above", "below")}</div>
        <div class="delta-chip" style={`border-color:${globalMaleColor}; color:${globalMaleColor};`}>{deltaText(hieDeltaMale, "above", "below")}</div> -->

      </div>
      <div class="grid-bg"></div>
      <div class="bars">
        <div class="flex flex-col gap-0 mb-[1px]">
          <span class='text-xs'>Women</span>
          <div class="bar-row">
            <div class="bar global last-year" style={`--bar-clr:${global2024Color}; --target:${revealed ? percentOrMinWidth(hieRatioFemale.b) : '0%'};`}>
              <span class="end-label">{round(globalFemaleAvgHie2024 ?? 0, 1)} kJ</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalFemaleAvgHie2024 ?? 0} kJ in 2024</span> -->
        </div>
        <div class="flex flex-col gap-0 mb-[1px]">
          <div class="bar-row">
            <div class="bar global" style={`--bar-clr:${global2025Color}; --target:${revealed ? percentOrMinWidth(hieRatioFemale.a) : '0%'};`}>
              <span class="end-label">{round(globalFemaleAvgHie ?? 0, 1)} kJ</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalFemaleAvgHie2024 ?? 0} kJ in 2024</span> -->
        </div>
        <div class="flex flex-col gap-0 mb-[1px]">
          <span class='text-xs'>Men</span>
          <div class="bar-row">
            <div class="bar global last-year" style={`--bar-clr:${global2024Color}; --target:${revealed ? percentOrMinWidth(hieRatioMale.b) : '0%'};`}>
              <span class="end-label">{round(globalMaleAvgHie2024 ?? 0, 1)} kJ</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalMaleAvgHie2024 ?? 0} kJ in 2024</span> -->
        </div>
        <div class="flex flex-col gap-0 mb-[1px]">
          <div class="bar-row">
            <div class="bar global" style={`--bar-clr:${global2025Color}; --target:${revealed ? percentOrMinWidth(hieRatioMale.a) : '0%'};`}>
              <span class="end-label">{round(globalMaleAvgHie ?? 0, 1)} kJ</span>
            </div>
          </div>
          <!-- <span class='text-xs'>{globalMaleAvgHie2024 ?? 0} kJ in 2024</span> -->
        </div>
        <!-- <div class="bar-row">
          <div class="bar you" style={`--bar-clr:${youColor}; --target:${revealed ? percentOrMinWidth(hieRatios.userPct) : '0%'};`}>
            <span class="end-label">{round(userHie ?? 0, 1)} kJ</span>
          </div>
        </div> -->
      </div>
    </div>
  </div>
</div>

<style>
  /* Card chrome */
  .card-shell { position: relative; }
  .card-inner {
    position: relative; height: 100%; width: 100%;
    border-radius: 16px; overflow: hidden;
    box-shadow: inset 0 0 10px rgba(255,255,255,0.06), 0 14px 28px rgba(0,0,0,0.35);
    background:
      linear-gradient(135deg, rgba(12,14,18,0.95) 0%, rgba(14,16,20,0.96) 35%, rgba(18,20,26,0.96) 100%),
      radial-gradient(120px 160px at 20% 15%, rgba(255,255,255,0.06), transparent 60%),
      radial-gradient(120px 160px at 80% 90%, rgba(255,255,255,0.04), transparent 60%);
    outline: 1px solid rgba(255,255,255,0.05);
  }
  .drop-in { animation: drop-in 520ms ease-out both; }
  @keyframes drop-in {
    0% { opacity: 0; transform: translateY(3px) scale(0.98); }
    60%{ opacity: 1; transform: translateY(-1px) scale(1.01); }
    100%{ opacity: 1; transform: translateY(0) scale(1); }
  }

  /* Aurora sweep background */
  .glow-lines { position: absolute; inset: 0; pointer-events: none; will-change: transform; }
  .glow-lines::before, .glow-lines::after {
    content: ""; position: absolute; inset: 0;
    background:
      linear-gradient(140deg, rgba(94,230,232,0.18) 0%, rgba(94,230,232,0.05) 35%, transparent 65%),
      repeating-linear-gradient(90deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 6px);
    mix-blend-mode: screen; filter: blur(0.2px);
  }
  .glow-lines::after {
    background:
      linear-gradient(30deg, rgba(124,250,198,0.22) 0%, rgba(124,250,198,0.06) 35%, transparent 70%),
      repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 10px);
    opacity: 0.8;
  }
  /* Woven texture overlay */
  .woven { position: absolute; inset: 0; pointer-events: none;
    background: repeating-linear-gradient(45deg, rgba(255,255,255,0.018) 0 12px, rgba(255,255,255,0.03) 12px 24px, rgba(255,255,255,0.018) 24px 36px);
    opacity: 0.7;
  }

  /* Header */
  .header { height: 60px; padding: 12px 14px; display: flex; align-items: center; justify-content: space-between; }
  .title { font-size: 1.05rem; font-weight: 800; letter-spacing: 0.3px; }
  .legend { display: flex; gap: 8px; align-items: center; }
  .chip { display: flex; gap: 6px; align-items: center; font-size: 0.85rem; padding: 6px 8px; border-radius: 999px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); }
  .dot { width: 8px; height: 8px; border-radius: 999px; box-shadow: 0 0 8px rgba(255,255,255,0.12); }

  /* Metric blocks */
  .metric-block { position: relative; padding: 10px 14px; }
  .label-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; gap: 2px; }
  .label { font-weight: 700; opacity: 0.95; }
  .delta-chip { font-size: 0.75rem; padding: 4px 8px; border-radius: 999px; border: 1px solid; background: rgba(255,255,255,0.04); }

  .grid-bg { position: absolute; inset: 42px 14px 26px 14px; border-radius: 10px; background:
    linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01)),
    repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 38px);
    opacity: 0.35;
  }

  .bars { position: relative; display: flex; flex-direction: column; gap: 0px; }
  .bar-row { position: relative; height: 13px; }
  .bar { position: absolute; left: 14px; right: 14px; height: 100%; min-width: 13px; border-radius: 999px; background: rgba(255,255,255,0.06);
    box-shadow: inset 0 0 8px rgba(255,255,255,0.06);
  }
  .bar::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 0%; border-radius: 999px;
    background: linear-gradient(90deg, var(--bar-clr), color-mix(in srgb, var(--bar-clr), white 12%));
    box-shadow: 0 0 8px color-mix(in srgb, var(--bar-clr), white 24%);
    transition: width 560ms ease-out;
    width: var(--target, 0%);
  }
  /* Using inline CSS var --target to drive fill width */
  /* numeric label */
  .end-label { position: absolute; right: -6px; top: 50%; transform: translateY(-50%); font-size: 0.8rem; font-weight: 700; text-shadow: 0 0 8px rgba(0,0,0,0.35); }

  .global { filter: saturate(90%); }
  .you { filter: saturate(100%); }

  /* .bar.last-year {
    opacity: 0.8;
  } */

  .bar:not(.last-year)::after { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 0%; border-radius: 999px;
    background: repeating-linear-gradient(
      45deg,
      rgba(255,255,255,0.3) 0px,
      rgba(255,255,255,0.3) 2px,
      transparent 2px,
      transparent 8px
    );
    transition: width 560ms ease-out;
    width: var(--target, 0%);
  }

  /* Tooltip icon */
  .tooltip { position: absolute; left: -18px; top: 50%; transform: translateY(-50%); font-size: 0.8rem; opacity: 0.7; }

  /* Footer */
  .footer { height: 34px; padding: 8px 14px; display: flex; align-items: center; font-size: 0.8rem; opacity: 0.9; }
  .note { opacity: 0.85; }

</style>
